import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaInstagram, FaLinkedinIn, FaWhatsapp } from 'react-icons/fa';
import '../csscomponent/Footer.css';
import logo from '../assest/logo.png';
import GrooveLogoBadge from './GrooveLogoBadge';
import AdMobBanner from './AdMobBanner';
import { getCmsSettings, getFooterMenu } from '../lib/sqlAuth';
import { subscribeCmsUpdates } from '../lib/cmsSync';

const FALLBACK_FOOTER_LINKS = [
  { id: 'footer-home', label: 'Home', url: '/', target: '_self' },
  { id: 'footer-services', label: 'Services', url: '/services', target: '_self' },
  { id: 'footer-team', label: 'My Team', url: '/about', target: '_self' },
  { id: 'footer-product', label: 'Products', url: '/product', target: '_self' },
  { id: 'footer-contact', label: 'Contact', url: '/contact', target: '_self' },
  { id: 'footer-privacy', label: 'Privacy Policy', url: '/privacy-policy', target: '_self' },
  { id: 'footer-terms', label: 'Terms & Conditions', url: '/terms-of-service', target: '_self' },
];

function isExternalUrl(url = '') {
  return /^(https?:)?\/\//i.test(String(url)) || String(url).startsWith('mailto:') || String(url).startsWith('tel:');
}

function normalizeMenuUrl(url = '') {
  const nextUrl = String(url || '/').trim() || '/';
  return nextUrl === '/' ? '/home' : nextUrl;
}

export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();
  const [content, setContent] = useState({
    brandTitle: 'Services and Developer',
    brandDescription: 'Designing, developing, and scaling modern digital products.',
    copyright: '@2025 Services and Developer. All rights reserved.',
  });
  const [links, setLinks] = useState([]);

  const handleFooterLink = (event, url = '/', target = '_self') => {
    const nextUrl = normalizeMenuUrl(url);
    if (isExternalUrl(nextUrl) || target === '_blank') {
      return;
    }

    event.preventDefault();
    const [pathPart, hashPart = ''] = nextUrl.split('#');
    const normalizedPath = pathPart || '/';

    if (normalizedPath === location.pathname) {
      if (hashPart) {
        const targetElement = document.getElementById(hashPart);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    navigate(nextUrl);
  };

  useEffect(() => {
    let mounted = true;

    const loadFooter = () => {
      Promise.all([
        getCmsSettings({ keys: ['footer_brand_title', 'footer_brand_description', 'footer_copyright'] }),
        getFooterMenu(),
      ])
        .then(([settingsRes, menuRes]) => {
          if (!mounted) return;

          const settings = settingsRes?.settings || {};
          setContent((prev) => ({
            brandTitle: settings.footer_brand_title || prev.brandTitle,
            brandDescription: settings.footer_brand_description || prev.brandDescription,
            copyright: settings.footer_copyright || prev.copyright,
          }));

          const menus = Array.isArray(menuRes?.menus) ? menuRes.menus : [];
          const footerMenu = menus.find((menu) => menu.locationKey === 'footer') || menus[0];
          const nextLinks = Array.isArray(footerMenu?.items) ? footerMenu.items : [];
          setLinks(nextLinks.length > 0 ? nextLinks : FALLBACK_FOOTER_LINKS);
        })
        .catch(() => {
          if (!mounted) return;
          setLinks(FALLBACK_FOOTER_LINKS);
        });
    };

    loadFooter();
    const unsubscribe = subscribeCmsUpdates(() => loadFooter());

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  return (
    <footer className="custom-footer">
      <div className="footer-wrapper">
        <div className="footer-brand-banner">
          <div className="footer-brand-main">
            <img src={logo} alt="Services and Developer Logo" className="footer-logo" />
            <div className="footer-brand-copy">
              <span className="footer-kicker">Digital Product Studio</span>
              <span className="brand-name1">{content.brandTitle}</span>
              <p>{content.brandDescription}</p>
            </div>
          </div>
          <GrooveLogoBadge className="footer-groove-logo" />
        </div>

        <AdMobBanner className="footer-admob-banner" title="Sponsored Showcase" subtitle="Services & Development Partners" />

        <div className="footer-bottom-grid">
          <div className="footer-info-panel">
            <span className="footer-links-kicker">Why Clients Stay</span>
            <h3>Clear delivery, fast iteration, and practical support.</h3>
            <p>We build websites, CMS platforms, and digital products with direct communication and quality-first execution.</p>
            <div className="footer-meta">
              <span>Startup-friendly timelines</span>
              <span>CMS and product delivery</span>
              <span>Launch and support coverage</span>
            </div>
            <div className="footer-socials">
              <a href="https://www.instagram.com/saipan_naikwadi" target="_blank" rel="noreferrer" aria-label="Instagram">
                <FaInstagram />
              </a>
              <a href="https://www.linkedin.com/in/saipannaikwadi" target="_blank" rel="noreferrer" aria-label="LinkedIn">
                <FaLinkedinIn />
              </a>
              <a href="https://wa.me/918177966877" target="_blank" rel="noreferrer" aria-label="WhatsApp">
                <FaWhatsapp />
              </a>
            </div>
          </div>
          <div className="footer-links-panel">
            <div className="footer-links-head">
              <span className="footer-links-kicker">Quick Links</span>
              <p>Explore our company, services, team, and legal pages.</p>
            </div>
            <ul className="footer-links">
              {(links.length > 0 ? links : FALLBACK_FOOTER_LINKS).map((link) => (
                <li key={link.id || link.label}>
                  <Link
                    to={link.url || '/'}
                    target={link.target || '_self'}
                    onClick={(event) => handleFooterLink(event, link.url || '/', link.target || '_self')}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <hr className="footer-divider" />

        <p className="footer-copyright">{content.copyright}</p>
      </div>
    </footer>
  );
}
